// @givmo/app-admin — Company direct-giving decode/build helpers (EP-WEB1). ★ MONEY-MOVING ★
//
// Pure, side-effect-free helpers for the company DIRECT-DONATION flow — a company gifting its OWN
// money (funding_source="company") to a charity / ETF / fundraiser, independent of matching. Kept as
// their own no-React module so the target mapping, the funding-body construction, the wallet
// sufficiency check, and the receipt-copy fork are all unit-testable in isolation (matching the
// `dashboard-decode.ts` / `payment-methods-decode.ts` split).
//
// Numbers-are-the-law: money crosses the wire as a decimal STRING and is only formatted or compared
// as integer cents via string math — never parsed to a float or re-rounded. Tolerant reader: the
// target decodes go through the shared `extractList`, which reads BOTH the bare row array the
// api-client response middleware actually delivers (it unwraps the backend's `{data,meta,links}`
// envelope, so the generated `XxxPaginatedResponse` type is a compile-time fiction at runtime) AND an
// un-unwrapped `{ data: [...] }` envelope — degrading to `[]` rather than throwing.
//
// Backend contract (`_resolve_company_funded_company`, CAM-BE4): a company-funded donation is booked
// when the body carries `funding_source="company"` (+ the backend flag ON, + an active company admin
// holding `company:match_donation`, + a WALLET/CREDIT_CARD/APPLE_PAY/GOOGLE_PAY method, + NOT
// `request_match`). We ALSO send `domain_type="company"`: the backend IGNORES `domain_type` when the
// gift resolves to company funds, but on the personal path it REJECTS `domain_type="company"` with a
// clean 400 — so if company-funding ever fails to resolve (flag off, permission lost, company
// inactive) the donation is rejected, never silently charged to the admin's PERSONAL funds.

import {
  donorTimezone,
  formatMoneyDisplay,
  type CharityOut,
  type EtfOut,
  type FundraiserOut,
  type GivmoError,
  type PaymentMethod,
  type WalletOut,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { disclosureEcho, type ResolvedDisclosure } from '@givmo/disclosure';
import type { StatedCharge } from '../../components/charge-quote';
import { parseCentsAmount } from '../dashboard/dashboard-decode';
import { cardLabel, type PaymentCard } from '../settings/payment-methods-decode';

// Re-export the shared whole-dollar money helpers so the give feature has ONE import surface for its
// amount semantics (identical to the match-approve amount rules — whole-dollar positive integers).
export { parseWholeDollarAmount, formatWholeDollarUsd } from '../dashboard/dashboard-decode';

// ── Targets ──────────────────────────────────────────────────────────────────────────────────────

/** The three direct-giving target kinds (D-EP-2). Collections + recurring are backend-rejected and
 *  are deliberately NOT offered by this UI. */
export type TargetType = 'charity' | 'etf' | 'fundraiser';

/** A search-result target reduced to exactly what the picker renders + the donation route needs. */
export interface GiveTarget {
  type: TargetType;
  /** The entity id — the `{charity_id}`/`{etf_id}`/`{fundraiser_id}` path param. */
  id: string;
  /** Display name (falls back to a generic label when the wire omits it). */
  name: string;
  /** Logo/avatar url, or null (the Avatar then renders initials from `name`). */
  logoUrl: string | null;
  /** Optional secondary line (e.g. a charity category); null when absent. */
  subtitle: string | null;
  /** How many charities an ETF bundles (its `charities` on the wire); absent or null otherwise. */
  charityCount?: number | null;
}

const TARGET_FALLBACK_NAME: Record<TargetType, string> = {
  charity: 'Charity',
  etf: 'Fund',
  fundraiser: 'Fundraiser',
};

function nonEmpty(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function mapCharity(charity: CharityOut): GiveTarget | null {
  const id = nonEmpty(charity.id);
  if (id === null) return null;
  return {
    type: 'charity',
    id,
    name: nonEmpty(charity.name) ?? TARGET_FALLBACK_NAME.charity,
    logoUrl: nonEmpty(charity.logo_url),
    subtitle: nonEmpty(charity.category),
  };
}

function mapEtf(etf: EtfOut): GiveTarget | null {
  const id = nonEmpty(etf.id);
  if (id === null) return null;
  return {
    type: 'etf',
    id,
    name: nonEmpty(etf.name) ?? TARGET_FALLBACK_NAME.etf,
    logoUrl: nonEmpty(etf.logo_url),
    subtitle: 'Fund',
    charityCount: Array.isArray(etf.charities) ? etf.charities.length : null,
  };
}

function mapFundraiser(fundraiser: FundraiserOut): GiveTarget | null {
  const id = nonEmpty(fundraiser.id);
  if (id === null) return null;
  return {
    type: 'fundraiser',
    id,
    name: nonEmpty(fundraiser.name) ?? TARGET_FALLBACK_NAME.fundraiser,
    // FundraiserOut carries no logo on this list surface — the Avatar renders initials.
    logoUrl: null,
    subtitle: 'Fundraiser',
  };
}

/** Tolerant decode of `GET /charities` → charity targets; drops entries without an id, never throws. */
export function extractCharityTargets(body: unknown): GiveTarget[] {
  return mapList(extractList<CharityOut>(body), mapCharity);
}

/** Tolerant decode of `GET /etfs` → ETF targets. */
export function extractEtfTargets(body: unknown): GiveTarget[] {
  return mapList(extractList<EtfOut>(body), mapEtf);
}

/** Tolerant decode of `GET /fundraisers` → fundraiser targets. */
export function extractFundraiserTargets(body: unknown): GiveTarget[] {
  return mapList(extractList<FundraiserOut>(body), mapFundraiser);
}

function mapList<T>(raw: T[], map: (item: T) => GiveTarget | null): GiveTarget[] {
  const out: GiveTarget[] = [];
  for (const item of raw) {
    const mapped = map(item);
    if (mapped !== null) out.push(mapped);
  }
  return out;
}

/**
 * Parse the give route's optional `?fundraiserId=` search param (the EP-WEB3 "Seed this fundraiser"
 * CTA's preselect seam). STRICT: only a non-empty string survives (trimmed); numbers, arrays,
 * objects, booleans, and blank/whitespace values are dropped so junk in the URL can never poison the
 * preselect query — an absent param means the ordinary interactive target pick. Pure (used as the
 * route's `validateSearch`).
 */
export function parseGiveSearch(search: Record<string, unknown>): { fundraiserId?: string } {
  const raw = search.fundraiserId;
  const id = typeof raw === 'string' ? raw.trim() : '';
  return id.length > 0 ? { fundraiserId: id } : {};
}

// ── Funding ──────────────────────────────────────────────────────────────────────────────────────

/** The chosen funding source for the gift: a specific saved company card, or the company wallet. */
export type Funding = { kind: 'card'; cardId: string } | { kind: 'wallet' };

/** The wire `payment_method` for a funding source. Card → "Credit Card"; wallet → "Wallet". */
export function fundingPaymentMethod(funding: Funding): PaymentMethod {
  return funding.kind === 'wallet' ? 'Wallet' : 'Credit Card';
}

/** The company wallet reduced to what the payment picker + the sufficiency pre-check need. */
export interface WalletFunds {
  /** Formatted spendable balance with a leading `$` (e.g. "$1,250.00"); falls back to "$0.00". */
  display: string;
  /**
   * Spendable balance in integer CENTS (available_balance when present — balance minus active holds —
   * else balance), or null when the wire value is absent/unparseable. Used ONLY for the
   * insufficient-funds pre-check comparison; never re-formatted back into displayed money.
   */
  availableCents: number | null;
}

/** Map a `WalletOut` (or undefined while pending/errored) to the funds view. Tolerant: an absent /
 *  unparseable wallet yields a "$0.00" display + null cents (no pre-check, backend stays authoritative). */
export function toWalletFunds(wallet: WalletOut | undefined): WalletFunds {
  const availableRaw =
    nonEmpty(wallet?.available_balance) ??
    (typeof wallet?.balance === 'string' ? wallet.balance : null);
  const formatted = availableRaw !== null ? formatMoneyDisplay(availableRaw) : null;
  return {
    display: formatted !== null ? `$${formatted}` : '$0.00',
    availableCents: availableRaw !== null ? parseCentsAmount(availableRaw) : null,
  };
}

/**
 * Whether the company wallet is KNOWN to cover a whole-dollar gift amount. Returns `true` when the
 * balance is unknown (`availableCents === null`) — we only PRE-SURFACE insufficiency when we can prove
 * it; the backend `InsufficientFundsException` (409) is the authoritative guard either way. Integer
 * comparison only — no float math on money.
 */
export function walletCoversAmount(availableCents: number | null, amountDollars: number): boolean {
  if (availableCents === null) return true;
  if (!Number.isInteger(amountDollars) || amountDollars <= 0) return true;
  return availableCents >= amountDollars * 100;
}

/**
 * The human-readable funding label restated in the confirm copy. TOLERANT of a vanished card: if the
 * selected card is no longer in the list (deleted in another tab, a refetch emptied the list), fall
 * back to a generic label rather than throwing mid-render — the backend rejects a charge against a
 * gone card anyway, so the label is display-only.
 */
export function fundingDisplayLabel(
  funding: Funding | null,
  cards: PaymentCard[],
  walletDisplay: string,
): string {
  if (funding === null) return '';
  if (funding.kind === 'wallet') return `your company wallet (${walletDisplay})`;
  const card = cards.find((c) => c.id === funding.cardId);
  return card !== undefined ? `your ${cardLabel(card)}` : 'your saved company card';
}

/**
 * The funding source as the confirm dialog's summary row names it (D-CAM-41): the card itself
 * ("Visa Card •••• 4242") or the company wallet with its spendable balance when that is known
 * ("Company wallet ($12,500.00 available)"), with the same vanished-card tolerance as
 * {@link fundingDisplayLabel}. A noun phrase, not the sentence form ("your …") the confirm copy uses.
 */
export function fundingSummaryLabel(
  funding: Funding | null,
  cards: PaymentCard[],
  wallet?: WalletFunds,
): string {
  if (funding === null) return '';
  if (funding.kind === 'wallet') {
    return wallet !== undefined && wallet.availableCents !== null
      ? `Company wallet (${wallet.display} available)`
      : 'Company wallet';
  }
  const card = cards.find((c) => c.id === funding.cardId);
  return card !== undefined ? cardLabel(card) : 'Saved company card';
}

/**
 * The quiet line under the recipient in the Give summary (D-CAM-41), in the product's own names: a
 * Cause ETF says what it is and, when the wire carries its bundle, how many charities it holds; a
 * fundraiser says it is one. A charity gets none — its category adds nothing a donor needs here.
 */
export function giveSummaryContext(target: GiveTarget | null): string | null {
  if (target === null) return null;
  if (target.type === 'fundraiser') return 'Fundraiser';
  if (target.type !== 'etf') return null;
  const count = target.charityCount ?? null;
  return count !== null && count > 0
    ? `Cause ETF · ${count} ${count === 1 ? 'charity' : 'charities'}`
    : 'Cause ETF';
}

// ── Donation body ────────────────────────────────────────────────────────────────────────────────

/** The company-funded donation body shared by all three target routes (charity/ETF/fundraiser). The
 *  charity `DonationCreation` is the widest shape; the ETF/fundraiser bodies accept the same fields. */
export interface CompanyDonationBody {
  amount: number;
  domain_type: 'company';
  funding_source: 'company';
  payment_method: PaymentMethod;
  card_id: string | null;
  request_match: false;
  visibility: string;
  hide_name: boolean;
  instructions: string;
  message: string;
  metadata: Record<string, never>;
  cover_fees: boolean;
  alternative_payment_method_token: Record<string, never>;
  /** Silent C.3 device-zone capture — present only when the runtime resolved a zone (never null). */
  donor_timezone?: string;
  /** The shown disclosure's registry id + hash (CAM-X3) — present only when one was shown. */
  shown_disclaimer_version_id?: string;
  shown_disclaimer_content_hash?: string;
  /** The gross and fee the dialog stated from the server's quote (CAM-BE13) — present only then. */
  expected_amount_charged?: string;
  expected_fee?: string;
}

/**
 * Build the company-funded direct-donation body. `amount` is the whole-dollar integer (validated > 0
 * by the caller). `funding_source="company"` + `domain_type="company"` route it to company funds (and
 * fail-closed to a 400 if that resolution ever fails — never a silent personal charge);
 * `request_match=false` is REQUIRED (a company gift matching itself would double-count). Card →
 * `payment_method:"Credit Card"` + the `card_id`; wallet → `payment_method:"Wallet"` + `card_id:null`.
 * `disclosure` is the company-payer body the dialog showed (CAM-X3): its id + hash ride as the
 * `shown_disclaimer_*` echo the backend gate checks before any charge; absent, the body is unchanged.
 * `coverFees` is the admin's "Cover processing fees" choice and `stated` the gross and fee the dialog
 * stated for it from the server's quote (CAM-BE13): the backend holds the charge to them (409
 * `CHARGE_QUOTE_CHANGED` before any money moves); absent, the body is unchanged.
 */
export function buildCompanyDonationBody(params: {
  amount: number;
  funding: Funding;
  disclosure?: ResolvedDisclosure | null;
  coverFees?: boolean;
  stated?: StatedCharge | null;
}): CompanyDonationBody {
  const { amount, funding, disclosure, coverFees = false, stated = null } = params;
  return {
    amount,
    domain_type: 'company',
    funding_source: 'company',
    payment_method: fundingPaymentMethod(funding),
    card_id: funding.kind === 'card' ? funding.cardId : null,
    request_match: false,
    visibility: 'Public',
    hide_name: false,
    instructions: '',
    message: '',
    metadata: {},
    cover_fees: coverFees,
    alternative_payment_method_token: {},
    // Additive-optional SILENT device-zone capture (C.3 donor-local receipt dating): the acting
    // admin IS the donor here, so their IANA zone rides the body — zero UI, never asked. The
    // backend dates the receipt donor-local behind its dark flag (invalid/absent → the ET
    // fallback); a failed capture spreads `{}` (key omitted) so it can NEVER block the gift.
    ...donorTimezone(),
    ...disclosureEcho(disclosure),
    ...(stated ?? {}),
  };
}

// ── Receipt copy fork (plan §3 — LAW) ──────────────────────────────────────────────────────────────
//
// Company CARD ⇒ a company-named receipt is emailed to the acting admin (say so, NEUTRALLY — no
// deductibility language, which is counsel-gated). Company WALLET ⇒ receipt-LESS (the deposit was
// already receipted), so the success copy must NOT mention a receipt at all.

/** The success-state receipt line for the funding source, or null (wallet → no receipt mention). */
export function receiptCopy(funding: Funding): string | null {
  return funding.kind === 'card' ? 'A receipt for this donation will be emailed to you.' : null;
}

// ── Error classification ────────────────────────────────────────────────────────────────────────

/**
 * Whether a failed charge is the backend's INSUFFICIENT-FUNDS signal — a company WALLET gift that
 * exceeds the wallet balance. The backend maps `InsufficientFundsException` to HTTP **409** with the
 * stable title "Insufficient funds" (errorhandling.py). This is a DEFINITE no-charge (a plain 409 is
 * fatal, never retryable), surfaced as its own first-class state distinct from a generic decline.
 */
export function isInsufficientFundsError(error: GivmoError): boolean {
  if (error.status !== 409) return false;
  return error.entries.some((e) => e.title === 'Insufficient funds');
}
